/**
 * CALCULATION FACTORY — math helpers only (not inventory / profit write truth).
 * Call sites: domain/finance, domain/inventory/valuation, domain/inventory/stock.
 * UI/pages must use those domain owners (ESLint restricted) — ADR-0013 / ADR-0016.
 */

import { normalizeSale, normalizePurchase, normalizeExpense, normalizeLots } from '../utils/dataNormalizer';
import {
  indexLotsByProduct,
  aggregateLotsMetrics,
  lotProductKey,
  lotLocationKey
} from '../domain/inventory/lots/lotsIndex';
import { dedupeLotsList } from '../utils/lotSystem';
import {
  getSaleLineActiveQuantity,
  isSaleFullyCancelled
} from '../domain/sales/saleLineMath';
import { sumRevenue } from '../domain/finance/revenue';
import {
  resolveSaleInventoryCogs,
  sumStockConsumptionCosts
} from '../domain/finance/saleInventoryCost';
import { isActivePurchaseRow } from '../domain/purchases/purchaseVoidStatus';

function asActivePurchases(purchases) {
  return (Array.isArray(purchases) ? purchases : [])
    .map(normalizePurchase)
    .filter(isActivePurchaseRow);
}

function costFromUsedLots(saleProduct, activeQty) {
  const used = saleProduct?.usedLots;
  if (!Array.isArray(used) || used.length === 0 || !(activeQty > 0)) return null;
  let lotQty = 0;
  let lotCost = 0;
  for (let i = 0; i < used.length; i += 1) {
    const u = used[i];
    const q = Number(u?.quantity) || 0;
    const price = Number(u?.unitPrice ?? u?.purchaseUnitPrice) || 0;
    if (q <= 0) continue;
    lotQty += q;
    lotCost += q * price;
  }
  if (!(lotQty > 0) || !(lotCost > 0)) return null;
  // Partial void: scale snapshot cost to still-active qty
  const scale = Math.min(1, activeQty / lotQty);
  return lotCost * scale;
}

class CalculationFactory {
  /**
   * Lot-metrics math helper (used via domain/inventory/stock.projectStockFromLots).
   */
  static calculateStockFromLots(productId, location, lots) {
    const lotsArray = dedupeLotsList(normalizeLots(lots));
    const wantLocation = location ? lotLocationKey(location) : null;
    const productLots = lotsArray.filter((lot) => {
      if (!lot || typeof lot !== 'object') return false;
      const matchesProduct = String(lot.productId) === String(productId);
      // Case-insensitive location match (mirrors getLotsForProduct).
      const matchesLocation =
        !wantLocation || lotLocationKey(lot.location) === wantLocation;
      return matchesProduct && matchesLocation;
    });
    return aggregateLotsMetrics(productLots);
  }
  
  /**
   * Inventory value math helper (domain/inventory/valuation prefers snapshots when present).
   * Lot-FIFO sellable sum over lots (ghost clones collapsed) — O(lots + products).
   */
  static calculateInventoryValue(products, lots) {
    const lotsArray = dedupeLotsList(normalizeLots(lots));
    const productList = Array.isArray(products) ? products : [];
    const lotsByProduct = indexLotsByProduct(lotsArray);

    // Dashboard / Inventaire total must include every sellable lot (orphan productId ok).
    const allMetrics = aggregateLotsMetrics(lotsArray);
    const totalValue = allMetrics.value;
    const totalPotentialProfit = allMetrics.potentialProfit;

    const byCategory = {};
    const byLocation = {};
    const countedLotIds = new Set();

    const bump = (bucket, key, value, potentialProfit, productsInc = 0) => {
      if (!bucket[key]) {
        bucket[key] = { value: 0, potentialProfit: 0, products: 0 };
      }
      bucket[key].value += value;
      bucket[key].potentialProfit += potentialProfit;
      bucket[key].products += productsInc;
    };

    for (const product of productList) {
      const keyId = lotProductKey(product?.id);
      const keyPid =
        product?.productId != null && product.productId !== ''
          ? lotProductKey(product.productId)
          : '';
      const fromId = lotsByProduct.get(keyId) || [];
      const fromPid =
        keyPid && keyPid !== keyId ? lotsByProduct.get(keyPid) || [] : [];
      const productLots =
        fromPid.length === 0
          ? fromId
          : (() => {
              const seen = new Set();
              const merged = [];
              for (const lot of fromId.concat(fromPid)) {
                const id = lot?.id != null ? String(lot.id) : null;
                if (id) {
                  if (seen.has(id)) continue;
                  seen.add(id);
                }
                merged.push(lot);
              }
              return merged;
            })();

      for (const lot of productLots) {
        if (lot?.id != null) countedLotIds.add(String(lot.id));
      }

      const metrics = aggregateLotsMetrics(productLots);
      const category = product.category || 'Uncategorized';
      bump(byCategory, category, metrics.value, metrics.potentialProfit, 1);
    }

    // Location breakdown from lots (product.location is often missing/wrong)
    for (const lot of lotsArray) {
      const metrics = aggregateLotsMetrics([lot]);
      if (!metrics.value && !metrics.potentialProfit) continue;
      const location = lot.location || 'Principal';
      bump(byLocation, location, metrics.value, metrics.potentialProfit, 0);
    }

    // Unmatched lots (no catalog product) — keep category bucket honest vs total.
    // Reconciliation: catalogValue + orphanValue === totalValue (all-lots).
    let orphanValue = 0;
    let orphanProfit = 0;
    let orphanCount = 0;
    if (countedLotIds.size < lotsArray.length) {
      for (const lot of lotsArray) {
        if (lot?.id != null && countedLotIds.has(String(lot.id))) continue;
        const metrics = aggregateLotsMetrics([lot]);
        orphanValue += metrics.value;
        orphanProfit += metrics.potentialProfit;
        if (metrics.value || metrics.potentialProfit) orphanCount += 1;
      }
      if (orphanValue || orphanProfit) {
        bump(byCategory, 'Uncategorized', orphanValue, orphanProfit, 0);
      }
    }

    return {
      totalValue,
      potentialProfit: totalPotentialProfit,
      byCategory,
      byLocation,
      // Explicit split so Bilan (all-lots) and Inventaire (catalog) are reconcilable.
      orphanValue,
      orphanProfit,
      orphanCount,
      catalogValue: totalValue - orphanValue,
      catalogPotentialProfit: totalPotentialProfit - orphanProfit
    };
  }
  
  /**
   * Profit math helper (domain/finance.calculateProfit owns the public surface).
   * COGS: frozen sale cost first, usedLots capture, then FIFO pool.
   */
  static calculateProfit(sales, purchases, expenses = [], lots = [], stockConsumptions = []) {
    const normalizedSales = sales.map(normalizeSale);
    const normalizedPurchases = asActivePurchases(purchases);
    const normalizedExpenses = expenses.map(normalizeExpense);
    const normalizedLots = normalizeLots(lots);
    
    // Revenue via domain sumRevenue (void-/restaurant-aware)
    const revenue = sumRevenue(sales);
    
    // ✅ Purchase Costs: prefer frozen sale COGS (ADR-0016), usedLots capture, then FIFO last
    let purchaseCosts = 0;
    let usedInventoryCost = false;
    let usedFifoFallback = false;
    let usedUsedLotsCapture = false;
    
    if (normalizedLots.length > 0) {
      // Index + FIFO-sort once per product — never filter/sort full lots per sale line
      const lotsByProductFifo = new Map();
      for (const lot of normalizedLots) {
        if (!lot || lot.productId == null) continue;
        const key = String(lot.productId);
        let bucket = lotsByProductFifo.get(key);
        if (!bucket) {
          bucket = [];
          lotsByProductFifo.set(key, bucket);
        }
        bucket.push(lot);
      }
      for (const bucket of lotsByProductFifo.values()) {
        bucket.sort((a, b) => {
          const dateA = new Date(a.purchaseDate || a.createdAt || 0);
          const dateB = new Date(b.purchaseDate || b.createdAt || 0);
          return dateA - dateB;
        });
      }

      // Shared sold-capacity pool per lot across sales — do not re-attribute
      // full (total−remaining) to every sale (multi-sale overstatement).
      // Prefer frozen/usedLots; this is last-resort legacy only.
      const soldCapLeftByLot = new Map();
      for (const bucket of lotsByProductFifo.values()) {
        for (const lot of bucket) {
          const id = lot?.id != null ? String(lot.id) : null;
          if (!id) continue;
          const totalQty = Number(lot.totalQuantity || 0);
          const remainingQty = Number(lot.remainingQuantity || 0);
          soldCapLeftByLot.set(id, Math.max(0, totalQty - remainingQty));
        }
      }

      // Void/cancel: nur aktive Mengen; stornierte Sales zählen 0.
      sales.forEach((rawSale) => {
        if (isSaleFullyCancelled(rawSale)) return;
        const fromSaleCost = resolveSaleInventoryCogs(rawSale);
        if (fromSaleCost != null) {
          purchaseCosts += fromSaleCost;
          usedInventoryCost = true;
          return;
        }
        const sale = normalizeSale(rawSale);
        if (sale.products && Array.isArray(sale.products)) {
          sale.products.forEach(saleProduct => {
            const productId = saleProduct.productId || saleProduct.id;
            const quantitySold = getSaleLineActiveQuantity(saleProduct);
            
            if (quantitySold > 0) {
              const fromUsed = costFromUsedLots(saleProduct, quantitySold);
              if (fromUsed != null) {
                purchaseCosts += fromUsed;
                usedUsedLotsCapture = true;
                return;
              }

              usedFifoFallback = true;
              const productLots = lotsByProductFifo.get(String(productId)) || [];
              
              let costForProduct = 0;
              let remainingToCalculate = quantitySold;
              
              for (const lot of productLots) {
                if (remainingToCalculate <= 0) break;
                const lotId = lot?.id != null ? String(lot.id) : null;
                if (!lotId) continue;
                const capLeft = soldCapLeftByLot.get(lotId) || 0;
                if (!(capLeft > 0)) continue;
                const lotPrice = Number(lot.purchaseUnitPrice || 0);
                if (!(lotPrice > 0)) continue;
                const usedQuantity = Math.min(remainingToCalculate, capLeft);
                costForProduct += usedQuantity * lotPrice;
                remainingToCalculate -= usedQuantity;
                soldCapLeftByLot.set(lotId, capLeft - usedQuantity);
              }
              
              purchaseCosts += costForProduct;
            }
          });
        }
      });
    } else {
      // Keine Lots: frozen sale COGS, else purchase totals (unsold inventory not COGS)
      let fromSales = 0;
      let anyInventoryCost = false;
      sales.forEach((rawSale) => {
        if (isSaleFullyCancelled(rawSale)) return;
        const fromSaleCost = resolveSaleInventoryCogs(rawSale);
        if (fromSaleCost != null) {
          fromSales += fromSaleCost;
          anyInventoryCost = true;
        } else {
          usedFifoFallback = true;
        }
      });
      if (anyInventoryCost) {
        purchaseCosts = fromSales;
        usedInventoryCost = true;
      } else if (normalizedSales.length === 0) {
        purchaseCosts = 0;
      } else {
        purchaseCosts = normalizedPurchases.reduce((sum, purchase) => sum + purchase.total, 0);
      }
    }

    const consumptionCosts = sumStockConsumptionCosts(stockConsumptions);
    if (consumptionCosts > 0) {
      purchaseCosts += consumptionCosts;
    }
    
    const operatingExpenses = normalizedExpenses.reduce((sum, expense) => sum + expense.amount, 0);
    
    const netProfit = revenue - purchaseCosts - operatingExpenses;
    const profitMargin = revenue > 0 ? (netProfit / revenue) * 100 : 0;

    let costSource = 'purchases';
    if (normalizedLots.length > 0) costSource = 'lots_fifo';
    if (usedInventoryCost && !usedFifoFallback && !usedUsedLotsCapture) {
      costSource = 'sale_frozen_cogs';
    } else if (usedInventoryCost && usedUsedLotsCapture && !usedFifoFallback) {
      costSource = 'mixed_frozen_used_lots';
    } else if (usedUsedLotsCapture && !usedFifoFallback) {
      costSource = 'sale_used_lots';
    } else if (usedInventoryCost && usedFifoFallback) {
      costSource = 'mixed_frozen_fifo';
    } else if (usedUsedLotsCapture && usedFifoFallback) {
      costSource = 'mixed_used_lots_fifo';
    } else if (usedInventoryCost) {
      costSource = 'sale_inventory_cost';
    }
    if (consumptionCosts > 0) {
      costSource = costSource === 'purchases' ? 'consumptions' : `${costSource}+consumptions`;
    }
    
    return {
      revenue,
      purchaseCosts,
      operatingExpenses,
      netProfit,
      profitMargin,
      totalCosts: purchaseCosts + operatingExpenses,
      costSource
    };
  }
  
}

export default CalculationFactory;

